import { DatePickerObj } from '@/models/utils/InputUtils.js'
import { defineStore } from 'pinia'
import { OrganizationModalStep } from '@/models/enums/OrganizationModalStep.js'
import { ref } from 'vue'
import { useStorage } from '@vueuse/core'

/**
 * Default org record data. Used when editing on admin page.
 * @type {{id: number, organization_name: string, organization_name_dba: string, street: string, facility_type: string, city: string, state: string, zip: string, phone: string, phone_input: string, npi: string, ccn: string, tin: string, date_opened: DatePickerObj, date_closed: DatePickerObj, use_dba: boolean}}
 */
export const defaultOrgData = {
	id: 0,
	organization_name: '',
	organization_name_dba: '',
	street: '',
	facility_type: '',
	city: '',
	state: '',
	zip: '',
	phone: '',//Value incoming from db, updated when saving
	phone_input: '',//Value shown in the input field
	npi: '',
	ccn: '',
	tin: '',
	date_opened: new DatePickerObj('', 'Y-m-d', 'm/d/Y'),
	date_closed: new DatePickerObj('', 'Y-m-d', 'm/d/Y'),
	use_dba: true
}

/**
 * Organization store used to track the adding of orgs to a user
 * @typedef {function} organizationStore {switchOrganizationToId: number, targetUserId: number, existingOrgKeys: Array<number>}
 * @property {Object} state - organizationStore state
 * @property {string} state.currentStep - Current step the modal is in
 * @property {boolean} state.showAddModalEh - Indicates whether the modal should be shown
 * @property {boolean} state.showChooseModalEh - Indicates whether the choose organization modal should be shown
 * @property {boolean} state.showAdvancedSearchEh - Indicates whether the advanced search fields should be shown
 * @property {number} state.switchOrganizationToId - Organization ID user is switching to
 * @property {number} state.targetUserId - User ID of user that organization is being added to
 * @property {Array<number>} state.existingOrgKeys Array of organization ids a user has been assigned
 * @property {number} state.currentPage Current page of the grid
 */
export const useOrganizationStore = defineStore('organizationStore', {
	state: () => ({
		currentStep: OrganizationModalStep.SEARCH,
		showAddModalEh: false,
		showChooseModalEh: false,
		showAdvancedSearchEh: false,
		switchOrganizationToId: 0,
		targetUserId: 0,
		existingOrgKeys: [],
		currentPage: useStorage('currentPage', ref(1)),
		table: null
	}),
	getters: {
		/**
		 * Returns true if the add organization modal should be shown
		 * @param {organizationStore.state} state
		 * @returns {boolean}
		 */
		getShowAddModal(state){
			return state.showAddModalEh
		},

		/**
		 * Returns true if the choose organization modal should be shown
		 * @param {organizationStore.state} state
		 * @returns {boolean}
		 */
		getShowChooseModal(state){
			return state.showChooseModalEh
		},

		/**
		 * Returns true if the advanced search should be shown
		 * @param {organizationStore.state} state
		 * @returns {boolean}
		 */
		getShowAdvancedSearch(state){
			return state.showAdvancedSearchEh
		},

		/**
		 * Returns the current step the modal is in
		 * @param {organizationStore.state} state
		 * @returns {string}
		 */
		getCurrentStep(state)
		{
			return state.currentStep
		},

		/**
		 * Returns the targetUserId as a number
		 * @param {organizationStore.state} state
		 * @returns {number} User ID if adding via admin page, 0 if via user menu
		 * @author AJL (2025-10-15)
		 */
		getTargetUserId: (state) => {
			return (typeof state.targetUserId === 'number') ? state.targetUserId : 0;
		},

		/**
		 * Returns array of org ids that a user currently has
		 * @param {organizationStore.state} state
		 * @returns {Array<number>}
		 * @author AJL (2025-10-15)
		 */
		getExistingOrgKeys: (state) => {

			return (Array.isArray(state.existingOrgKeys)) ? state.existingOrgKeys : [];
		},

		/**
		 * Returns organization user is switching to
		 * @param {organizationStore.state} state
		 * @returns {number}
		 * @author AJL (2025-10-15)
		 */
		getSwitchOrganizationToId: (state) => {

			return (state.switchOrganizationToId > 0) ? state.switchOrganizationToId : 0;
		},
		getCurrentPage: (state) => {
			return state.currentPage
		}
	},
	actions: {
		/**
		 * Cleanup store
		 */
		logout(){
			this.setCurrentStep(OrganizationModalStep.SEARCH)
			this.showAddModalEh = false
			this.toggleChooseModal(false)
			this.showAdvancedSearchEh = false
			this.setSwitchOrganizationToId(0)
			this.setTargetUserId(0)
			this.setExistingOrgKeys([])
		},

		/**
		 * Sets the current step in the search/add process
		 * @param {string} p_step
		 */
		setCurrentStep(p_step){
			this.currentStep = p_step;
		},

		showForceAddOrgModal()
		{
			this.toggleAddModal(true, OrganizationModalStep.START, 0, []);
		},

		showNoOrgModal()
		{
			this.toggleAddModal(true, OrganizationModalStep.NO_ACTIVE_ORGS, 0, []);
		},

		showAddOrgToUserModal()
		{
			this.toggleAddModal(true, OrganizationModalStep.SEARCH, 0, []);
		},

		/**
		 * Hide the add organization modal. Clear store data.
		 * @param p_user_id {number} User ID org is being added to but only if not current user
		 * @param p_existing_org_keys {Array<number>} Array of org keys belonging to current user (so they don't add them twice)
		 */
		showAdminAddOrgToUserModal(p_user_id, p_existing_org_keys)
		{
			this.toggleAddModal(true, OrganizationModalStep.SEARCH, p_user_id, p_existing_org_keys);
		},

		/**
		 * Hide the add organization modal. Clear store data.
		 * @param {boolean} p_show_eh True to show, false to hide
		 * @param {string} p_step OrganizationModalStep to show modal at
		 * @param p_user_id {number} User ID org is being added to but only if not current user
		 * @param p_existing_org_keys {Array<number>} Array of org keys belonging to current user (so they don't add them twice)
		 * @returns {boolean} True if a reload of data should occur
		 */
		toggleAddModal(p_show_eh, p_step, p_user_id, p_existing_org_keys)
		{
			let reload_eh = false;
			if (p_show_eh)
			{
				this.setCurrentStep(p_step);
				this.setTargetUserId(p_user_id);
				this.setExistingOrgKeys(p_existing_org_keys);
				this.showAddModalEh = true
			}
			else
			{
				if (this.currentStep === OrganizationModalStep.ADDED_ORG_TO_USER || this.currentStep === OrganizationModalStep.CREATED)
				{
					reload_eh = true
				}
				this.setCurrentStep(OrganizationModalStep.SEARCH);
				this.showAddModalEh = false
				this.setSwitchOrganizationToId(0)
				this.setTargetUserId(0)
				this.setExistingOrgKeys([])

				if (reload_eh)
				{
					this.reloadTable()
				}
			}

			return reload_eh;
		},

		/**
		 * Show or hide Choose organization modal
		 * @param {boolean} p_show_eh
		 */
		toggleChooseModal(p_show_eh)
		{
			this.showChooseModalEh = p_show_eh;
		},

		/**
		 * Shows or hides the advanced search fields
		 */
		toggleAdvancedSearch()
		{
			this.showAdvancedSearchEh = !this.showAdvancedSearchEh;
		},

		/**
		 * Set the switchOrganizationToId when user changes via right hand menu
		 * @param {number} p_organization_id Organization ID
		 */
		setSwitchOrganizationToId(p_organization_id)
		{
			if (typeof p_organization_id === 'number')
			{
				this.switchOrganizationToId = p_organization_id
			}
			else
			{
				this.switchOrganizationToId = 0
			}
		},

		/**
		 * Set the targetUserId when adding to another user via admin page
		 * @param p_user_id {number} User ID
		 */
		setTargetUserId(p_user_id)
		{
			if (typeof p_user_id === 'number')
			{
				this.targetUserId = p_user_id
			}
			else
			{
				this.targetUserId = 0
			}
		},

		/**
		 * Set the list of orgs a user already has so they are excluded from searching.
		 * @param p_org_keys {Array<number>} Array of org ids
		 */
		setExistingOrgKeys(p_org_keys)
		{
			if (Array.isArray(p_org_keys))
			{
				this.existingOrgKeys = p_org_keys
			}
			else
			{
				this.existingOrgKeys = []
			}
		},

		/**
		 * Set the current page of the grid
		 * @param {number} p_page Page number starting with 1
		 */
		setCurrentPage(p_page)
		{
			this.currentPage = p_page;
		},

		/**
		 * Set the User's EndpointTable so we can reload just the table after adding an organization
		 * @param p_table Endpoint table
		 */
		setUsersTable(p_table)
		{
			this.table = p_table
		},

		/**
		 * Reloads the table if it's set otherwise reloads the whole page.
		 */
		reloadTable()
		{
			if (this.table !== null)
			{
				this.table.loadData()
			}
			else
			{
				location.reload();
			}

		}
	}
})


/**
 * Organization store used to track the creating of orgs
 * @typedef {function} createOrganizationStore {currentStep: string, showModalEh: boolean}
 * @property {Object} state - createOrganizationStore state
 * @property {string} state.currentStep - Current step the modal is in
 * @property {boolean} state.showModalEh - Indicates whether the 'create' organization modal should be shown
 */

export const useCreateOrganizationStore = defineStore('createOrganizationStore', {
	state: () => ({
		currentStep: OrganizationModalStep.CREATE,
		showModalEh: false,
	}),
	getters: {

		/**
		 * Returns true if the 'create' organization modal should be shown
		 * @param {createOrganizationStore.state} state
		 * @returns {boolean}
		 */
		getShowModal(state){
			return state.showModalEh
		},

		/**
		 * Returns the current step the modal is in
		 * @param {createOrganizationStore.state} state
		 * @returns {string}
		 */
		getCurrentStep(state)
		{
			return state.currentStep
		},
	},
	actions: {
		/**
		 * Cleanup store
		 */
		logout(){
			this.showModalEh = false
			this.setCurrentStep(OrganizationModalStep.CREATE)
		},

		/**
		 * Sets the current step in the search/add process
		 * @param {string} p_step
		 */
		setCurrentStep(p_step){
			this.currentStep = p_step;
		},

		/**
		 * Show or Hide 'Create' modal
		 * @param {boolean} p_show_eh True to show, false to hide
		 */
		toggleCreateModal(p_show_eh)
		{
			if (p_show_eh)
			{
				this.setCurrentStep(OrganizationModalStep.CREATE)
				this.showModalEh = true;
			}
			else
			{
				let reload_eh = (this.currentStep === OrganizationModalStep.CREATED);
				this.setCurrentStep(OrganizationModalStep.CREATE);
				this.showModalEh = false;
				if (reload_eh)
				{
					location.reload()
				}
			}
		},

		/**
		 * Show Success Alert.
		 */
		showSuccessAlert()
		{
			this.setCurrentStep(OrganizationModalStep.CREATED);
		},

		/**
		 * Show Failed Alert.
		 */
		showFailedAlert()
		{
			this.setCurrentStep(OrganizationModalStep.FAILED);
		},

	}
})